<script lang="ts">
    import {onMount} from 'svelte';
    import {systemApi} from '../lib/api';
    import {t} from '../lib/i18n';
    import ApiLogsViewer from '../components/ApiLogsViewer.svelte';
    import TestApiLogging from '../components/TestApiLogging.svelte';
    import ApiLoggerSettings from '../components/ApiLoggerSettings.svelte';
    import OpenAIModelSelector from '../components/OpenAIModelSelector.svelte';
    import ConstantsManager from '../components/ConstantsManager.svelte';

    // Состояние компонента
    let loading = false;
    let error = '';
    let success = '';
    let apiStatus = 'unknown';
    let dataPointLimit = 1000;
    let showConfidenceIntervals = true;
    let defaultForecastAlgorithm = 'arima';
    let defaultConfidenceLevel = 0.95;
    let activeTab = 'general'; // 'general' (общие), 'api_logs' (API логи), 'constants' (константы), 'ai_models' (AI модели)

    // Загрузка настроек из локального хранилища при монтировании компонента
    onMount(async () => {
        loadSettings();
        await checkApiStatus();
    });

    // Проверка статуса API
    async function checkApiStatus() {
        try {
            loading = true;
            error = '';

            const response = await systemApi.healthCheck();
            apiStatus = response.status;

            loading = false;
        } catch (err) {
            error = err instanceof Error ? err.message : 'Не удалось проверить статус API';
            apiStatus = 'error';
            loading = false;
        }
    }

    // Загрузка настроек из локального хранилища
    function loadSettings() {
        try {
            const savedSettings = localStorage.getItem('timeseriesSettings');

            if (savedSettings) {
                const settings = JSON.parse(savedSettings);
                dataPointLimit = settings.dataPointLimit || 1000;
                showConfidenceIntervals = settings.showConfidenceIntervals !== undefined
                    ? settings.showConfidenceIntervals
                    : true;
                defaultForecastAlgorithm = settings.defaultForecastAlgorithm || 'arima';
                defaultConfidenceLevel = settings.defaultConfidenceLevel || 0.95;
            }
        } catch (err) {
            console.error('Ошибка загрузки настроек:', err);
        }
    }

    // Сохранение настроек в локальное хранилище
    function saveSettings() {
        try {
            const settings = {
                dataPointLimit,
                showConfidenceIntervals,
                defaultForecastAlgorithm,
                defaultConfidenceLevel
            };

            localStorage.setItem('timeseriesSettings', JSON.stringify(settings));

            success = t('Настройки успешно сохранены');
            setTimeout(() => {
                success = '';
            }, 3000);
        } catch (err) {
            error = t('Не удалось сохранить настройки');
        }
    }

    // Сброс настроек на значения по умолчанию
    function resetSettings() {
        if (confirm(t('Вы уверены, что хотите сбросить все настройки на значения по умолчанию?'))) {
            dataPointLimit = 1000;
            showConfidenceIntervals = true;
            defaultForecastAlgorithm = 'arima';
            defaultConfidenceLevel = 0.95;

            saveSettings();
        }
    }

</script>

<div class="settings-page">
    <h2 class="mb-4">{t('Настройки')}</h2>

    {#if error}
        <div class="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" class="btn-close" on:click={() => error = ''} aria-label={t('Закрыть')}></button>
        </div>
    {/if}

    {#if success}
        <div class="alert alert-success alert-dismissible fade show" role="alert">
            {success}
            <button type="button" class="btn-close" on:click={() => success = ''} aria-label={t('Закрыть')}></button>
        </div>
    {/if}

    <div class="tabs-container mb-4">
        <ul class="nav nav-tabs">
            <li class="nav-item">
                <button
                        class="nav-link"
                        class:active={activeTab === 'general'}
                        on:click={() => activeTab = 'general'}
                >
                    {t('Общие')}
                </button>
            </li>
            <li class="nav-item">
                <button
                        class="nav-link"
                        class:active={activeTab === 'constants'}
                        on:click={() => activeTab = 'constants'}
                >
                    {t('Константы')}
                </button>
            </li>
            <li class="nav-item">
                <button
                        class="nav-link"
                        class:active={activeTab === 'ai_models'}
                        on:click={() => activeTab = 'ai_models'}
                >
                    {t('Модели AI')}
                </button>
            </li>
            <li class="nav-item">
                <button
                        class="nav-link"
                        class:active={activeTab === 'api_logs'}
                        on:click={() => activeTab = 'api_logs'}
                >
                    {t('Логи API')}
                </button>
            </li>
            <li class="nav-item">
                <button
                        class="nav-link"
                        class:active={activeTab === 'system'}
                        on:click={() => activeTab = 'system'}
                >
                    {t('Система')}
                </button>
            </li>
        </ul>
    </div>

    <!-- Вкладка общих настроек -->
    {#if activeTab === 'general'}
        <div class="row">
            <div class="col-lg-8">
                <div class="card mb-4">
                    <div class="card-header">
                        <h5 class="mb-0">Настройки приложения</h5>
                    </div>
                    <div class="card-body">
                        <form on:submit|preventDefault={saveSettings}>

                            <div class="mb-3">
                                <label for="dataPointLimit" class="form-label">{t('Лимит отображения точек данных')}</label>
                                <div class="input-group">
                                    <input
                                            type="number"
                                            class="form-control"
                                            id="dataPointLimit"
                                            bind:value={dataPointLimit}
                                            min="100"
                                            max="10000"
                                            step="100"
                                    >
                                    <span class="input-group-text">{t('точек')}</span>
                                </div>
                                <div class="form-text">{t('Максимальное количество точек данных для отображения на графиках и в таблицах.')}</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-check form-switch">
                                    <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="showConfidenceIntervals"
                                            bind:checked={showConfidenceIntervals}
                                    >
                                    <label class="form-check-label"
                                           for="showConfidenceIntervals">{t('Показывать доверительные интервалы')}</label>
                                </div>
                                <div class="form-text">{t('Отображать доверительные интервалы на графиках прогнозов.')}</div>
                            </div>

                            <div class="mb-3">
                                <label for="defaultForecastAlgorithm"
                                       class="form-label">{t('Алгоритм прогнозирования по умолчанию')}</label>
                                <select
                                        class="form-select"
                                        id="defaultForecastAlgorithm"
                                        bind:value={defaultForecastAlgorithm}
                                >
                                    <option value="arima">ARIMA</option>
                                    <option value="ets">Экспоненциальное сглаживание (ETS)</option>
                                </select>
                                <div class="form-text">{t('Алгоритм, используемый для прогнозирования по умолчанию.')}</div>
                            </div>

                            <div class="mb-3">
                                <label for="defaultConfidenceLevel"
                                       class="form-label">{t('Уровень доверия по умолчанию')}</label>
                                <select
                                        class="form-select"
                                        id="defaultConfidenceLevel"
                                        bind:value={defaultConfidenceLevel}
                                >
                                    <option value="0.8">80%</option>
                                    <option value="0.9">90%</option>
                                    <option value="0.95">95%</option>
                                    <option value="0.99">99%</option>
                                </select>
                                <div class="form-text">{t('Уровень доверия по умолчанию для интервалов прогноза.')}</div>
                            </div>

                            <div class="d-flex justify-content-between">
                                <button type="submit" class="btn btn-primary">
                                    {t('Сохранить настройки')}
                                </button>
                                <button type="button" class="btn btn-outline-secondary" on:click={resetSettings}>
                                    {t('Сбросить на значения по умолчанию')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-lg-4">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{t('О системе')}</h5>
                    </div>
                    <div class="card-body">
                        <h6>{t('Платформа анализа временных рядов')}</h6>
                        <p class="text-muted">
                            {t('Комплексная система для анализа и прогнозирования временных рядов с современным веб-интерфейсом, REST API и интерактивной визуализацией.')}
                        </p>
                        <hr>
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{t('Фронтенд')}</dt>
                            <dd class="col-sm-8">{t('Svelte с TypeScript')}</dd>

                            <dt class="col-sm-4">{t('Бэкенд')}</dt>
                            <dd class="col-sm-8">{t('Python FastAPI')}</dd>

                            <dt class="col-sm-4">{t('База данных')}</dt>
                            <dd class="col-sm-8">{t('PostgreSQL')}</dd>

                            <dt class="col-sm-4">{t('ИИ')}</dt>
                            <dd class="col-sm-8">{t('OpenAI GPT-4o')}</dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <!-- Вкладка констант -->
    {#if activeTab === 'constants'}
        <div class="card">
            <div class="card-body p-0">
                <ConstantsManager/>
            </div>
        </div>
    {/if}

    <!-- Вкладка AI моделей -->
    {#if activeTab === 'ai_models'}
        <div class="card">
            <div class="card-header">
                <h5 class="mb-0">{t('Управление моделями OpenAI')}</h5>
            </div>
            <div class="card-body">
                <OpenAIModelSelector/>
            </div>
        </div>
    {/if}

    <!-- Вкладка API логов -->
    {#if activeTab === 'api_logs'}
        <div class="card">
            <div class="card-header">
                <h5 class="mb-0">{t('Логи API')}</h5>
            </div>
            <div class="card-body p-3">
                <div class="row">
                    <div class="col-md-3 mb-3 mb-md-0">
                        <div class="mb-4">
                            <TestApiLogging/>
                        </div>
                        <div>
                            <ApiLoggerSettings/>
                        </div>
                    </div>
                    <div class="col-md-9">
                        <ApiLogsViewer/>
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <!-- Вкладка системы -->
    {#if activeTab === 'system'}
        <div class="card">
            <div class="card-header">
                <h5 class="mb-0">{t('Состояние системы')}</h5>
            </div>
            <div class="card-body">
                <div class="system-status mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="mb-1">{t('Соединение с API')}</h6>
                            <p class="mb-0 text-muted">{t('Статус бэкенд API')}</p>
                        </div>
                        <div>
                            {#if apiStatus === 'healthy'}
                                <span class="badge bg-success">{t('Подключено')}</span>
                            {:else if apiStatus === 'error'}
                                <span class="badge bg-danger">{t('Отключено')}</span>
                            {:else if loading}
                                <span class="badge bg-secondary">{t('Проверка...')}</span>
                            {:else}
                                <span class="badge bg-warning">{t('Неизвестно')}</span>
                            {/if}
                        </div>
                    </div>
                </div>

                <div class="system-status mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="mb-1">{t('Версия')}</h6>
                            <p class="mb-0 text-muted">{t('Версия приложения')}</p>
                        </div>
                        <div>
                            <span class="badge bg-info">1.0.0</span>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <button
                            class="btn btn-outline-primary w-100"
                            on:click={checkApiStatus}
                            disabled={loading}
                    >
                        {#if loading}
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('Проверка...')}
                        {:else}
                            <i class="bi bi-arrow-repeat me-2"></i>
                            {t('Проверить соединение')}
                        {/if}
                    </button>
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
    .settings-page {
        margin-bottom: 2rem;
    }

    h2 {
        font-weight: 600;
    }

    .card {
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .form-text {
        font-size: 0.875rem;
        color: #6c757d;
    }

    .system-status {
        padding: 1rem;
        border-radius: 8px;
        background-color: #f8f9fa;
    }

    .system-status + .system-status {
        border-top: 1px solid #e9ecef;
    }

    dt {
        font-weight: 600;
        font-size: 0.875rem;
    }

    dd {
        font-size: 0.875rem;
    }

    /* Стили для вкладок */
    .tabs-container {
        margin-bottom: 1.5rem;
    }

    .nav-tabs {
        border-bottom: 1px solid #dee2e6;
    }

    .nav-tabs .nav-item {
        margin-bottom: -1px;
    }

    .nav-tabs .nav-link {
        border: 1px solid transparent;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
        padding: 0.5rem 1rem;
        cursor: pointer;
        color: #495057;
        background-color: transparent;
    }

    .nav-tabs .nav-link:hover {
        border-color: #e9ecef #e9ecef #dee2e6;
        color: #0d6efd;
    }

    .nav-tabs .nav-link.active {
        color: #495057;
        background-color: #fff;
        border-color: #dee2e6 #dee2e6 #fff;
        font-weight: 500;
    }

    /* Стили для темного режима (для будущей реализации) */
    :global(body.dark-mode) {
        background-color: #121212;
        color: #e0e0e0;
    }

    :global(body.dark-mode) .nav-tabs .nav-link.active {
        background-color: #1e1e1e;
        color: #fff;
        border-color: #444 #444 #1e1e1e;
    }

    :global(body.dark-mode) .nav-tabs .nav-link:hover {
        border-color: #444 #444 #444;
        color: #8bb4fe;
    }
</style>